import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/dialog";
import { Button } from "@/components/button";
import type { SubjectType } from "@/apis/exam_progress/types";
import { getPaperTypeLabel } from "@/constants/exam";

export type ExamTypeFilter = "all" | "SAT" | "ACT" | "AP";

export interface FilterState {
  examTypeFilter: ExamTypeFilter;
  typeFilter: "all" | SubjectType;
  difficultyFilter: "all" | "1" | "2" | "3" | "4" | "5";
}

interface FilteringExamPaperListModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterState) => void;
  currentFilters: FilterState;
}

const FilteringExamPaperListModal: React.FC<
  FilteringExamPaperListModalProps
> = ({ open, onClose, onApply, currentFilters }) => {
  // Temporary filter state for modal
  const [tempFilters, setTempFilters] = useState<FilterState>(currentFilters);

  // Update tempFilters when modal opens with current filters
  useEffect(() => {
    if (open) {
      setTempFilters(currentFilters);
    }
  }, [open, currentFilters]);

  const handleApply = () => {
    onApply(tempFilters);
    onClose();
  };

  const handleCancel = () => {
    setTempFilters(currentFilters);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} size="md">
      <DialogTitle>Filter Exam Papers</DialogTitle>
      <DialogBody className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Exam Type
          </label>
          <select
            value={tempFilters.examTypeFilter}
            onChange={(e) =>
              setTempFilters((prev) => ({
                ...prev,
                examTypeFilter: e.target.value as ExamTypeFilter,
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Exam Types</option>
            <option value="SAT">SAT</option>
            <option value="ACT">ACT</option>
            <option value="AP">AP</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subject
          </label>
          <select
            value={tempFilters.typeFilter}
            onChange={(e) =>
              setTempFilters((prev) => ({
                ...prev,
                typeFilter: e.target.value as FilterState["typeFilter"],
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Subjects</option>
            {(["reading_writing", "math", "english", "reading", "science"] as SubjectType[]).map(
              (subject) => (
                <option key={subject} value={subject}>
                  {getPaperTypeLabel(subject)}
                </option>
              )
            )}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Difficulty
          </label>
          <select
            value={tempFilters.difficultyFilter}
            onChange={(e) =>
              setTempFilters((prev) => ({
                ...prev,
                difficultyFilter: e.target
                  .value as FilterState["difficultyFilter"],
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Difficulties</option>
            <option value="1">Level 1</option>
            <option value="2">Level 2</option>
            <option value="3">Level 3</option>
            <option value="4">Level 4</option>
            <option value="5">Level 5</option>
          </select>
        </div>
      </DialogBody>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleApply}>Apply Filters</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilteringExamPaperListModal;
